import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Plus,
  Trash2,
  MapPin,
  Clock,
  CalendarRange,
  Loader2,
} from "lucide-react";

function formatDate(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function toISODate(d) {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function CalendarMaps() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(new Date());

  const [form, setForm] = useState({
    title: "",
    description: "",
    date: toISODate(new Date()),
    time: "",
    location_name: "",
    latitude: "",
    longitude: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/events");
      setEvents(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      if (!ev.date) continue;
      const k = ev.date.slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    }
    return map;
  }, [events]);

  const dayKey = toISODate(selectedDay);
  const dayEvents = eventsByDate.get(dayKey) || [];
  const upcoming = events
    .filter((e) => e.date >= toISODate(new Date()))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 6);

  const openCreate = () => {
    setForm({
      title: "",
      description: "",
      date: dayKey || toISODate(new Date()),
      time: "",
      location_name: "",
      latitude: "",
      longitude: "",
    });
    setError("");
    setDialogOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        date: form.date,
        time: form.time,
        location_name: form.location_name,
        latitude: form.latitude === "" ? null : parseFloat(form.latitude),
        longitude: form.longitude === "" ? null : parseFloat(form.longitude),
      };
      const { data } = await api.post("/calendar/events", payload);
      setEvents((prev) => [...prev, data].sort((a, b) => a.date.localeCompare(b.date)));
      setDialogOpen(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      // ignore
    }
  };

  return (
    <Layout title="Calendar · Maps" subtitle="calendar-maps-pod">
      <section className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-emerald-700 mb-2">
            Module · 02
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
            Plan events with place, all in one view.
          </h2>
          <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Schedule events, attach a location, and see what's coming up next.
            The calendar and the event list stay in perfect sync.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="calendar-create-event-button"
        >
          <Plus className="mr-1.5 h-4 w-4" /> New event
        </Button>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Calendar column */}
        <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="calendar-view">
          <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-3">
            Month view
          </div>
          <CalendarComponent
            mode="single"
            selected={selectedDay}
            onSelect={(d) => d && setSelectedDay(d)}
            modifiers={{
              hasEvent: Array.from(eventsByDate.keys()).map((k) => new Date(k)),
            }}
            modifiersClassNames={{
              hasEvent:
                "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-emerald-500",
            }}
            className="p-0"
          />

          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Selected
            </div>
            <div className="font-display text-lg font-semibold text-slate-900">
              {formatDate(dayKey)}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Events for selected day */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="calendar-day-events">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                  Day schedule
                </div>
                <div className="font-display text-lg font-semibold text-slate-900">
                  {formatDate(dayKey)}
                </div>
              </div>
              <div className="font-mono text-xs text-slate-500">
                {dayEvents.length} / {events.length}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : dayEvents.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 p-8 text-center">
                <CalendarRange className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                <div className="text-sm text-slate-500 mb-3">
                  Nothing scheduled for this day.
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCreate}
                  data-testid="calendar-empty-create"
                >
                  Add event
                </Button>
              </div>
            ) : (
              <ul className="space-y-3">
                {dayEvents.map((ev) => (
                  <li
                    key={ev.id}
                    data-testid={`event-item-${ev.id}`}
                    className="rounded-md border border-slate-200 bg-slate-50 p-4 flex items-start gap-4"
                  >
                    <div className="h-2 w-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base font-semibold text-slate-900 truncate">
                        {ev.title}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-600">
                        {ev.time && (
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" /> {ev.time}
                          </span>
                        )}
                        {ev.location_name && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" /> {ev.location_name}
                          </span>
                        )}
                        {ev.latitude != null && ev.longitude != null && (
                          <span className="font-mono text-[10px] text-slate-500">
                            {ev.latitude.toFixed?.(3)}, {ev.longitude.toFixed?.(3)}
                          </span>
                        )}
                      </div>
                      {ev.description && (
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                          {ev.description}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteEvent(ev.id)}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1"
                      data-testid={`event-delete-${ev.id}`}
                      aria-label="Delete event"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Map-like list */}
          <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="calendar-map-view">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                  Locations
                </div>
                <div className="font-display text-lg font-semibold text-slate-900">
                  Upcoming places
                </div>
              </div>
              <div className="font-mono text-xs text-slate-500">
                {upcoming.filter((e) => e.location_name).length} pinned
              </div>
            </div>

            <div className="relative rounded-md border border-slate-200 bg-grid-slate bg-slate-50 min-h-[220px] p-6 overflow-hidden">
              <div className="absolute inset-0 bg-grid-fade" aria-hidden />
              <div className="relative space-y-3">
                {upcoming.filter((e) => e.location_name).length === 0 ? (
                  <div className="text-center py-6">
                    <MapPin className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">
                      No locations yet. Add an event with a location to pin it
                      here.
                    </div>
                  </div>
                ) : (
                  upcoming
                    .filter((e) => e.location_name)
                    .map((e) => (
                      <div
                        key={e.id}
                        className="flex items-start gap-3 rounded-md border border-slate-200 bg-white px-4 py-3"
                        data-testid={`map-pin-${e.id}`}
                      >
                        <div className="h-7 w-7 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0">
                          <MapPin className="h-3.5 w-3.5 text-emerald-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-900 truncate">
                            {e.title}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600 truncate">
                            {e.location_name}
                          </div>
                        </div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 shrink-0">
                          {new Date(e.date).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="calendar-create-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New event</DialogTitle>
            <DialogDescription>
              Give it a title, a day, and a place.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ev-title">Title</Label>
              <Input
                id="ev-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Team sync"
                data-testid="event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ev-date">Date</Label>
                <Input
                  id="ev-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ev-time">Time</Label>
                <Input
                  id="ev-time"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  data-testid="event-time-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ev-location">Location name</Label>
              <Input
                id="ev-location"
                value={form.location_name}
                onChange={(e) =>
                  setForm({ ...form, location_name: e.target.value })
                }
                placeholder="Café Central, Berlin"
                data-testid="event-location-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ev-lat">Latitude</Label>
                <Input
                  id="ev-lat"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  placeholder="52.520"
                  data-testid="event-lat-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ev-lng">Longitude</Label>
                <Input
                  id="ev-lng"
                  value={form.longitude}
                  onChange={(e) =>
                    setForm({ ...form, longitude: e.target.value })
                  }
                  placeholder="13.405"
                  data-testid="event-lng-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ev-desc">Description</Label>
              <Textarea
                id="ev-desc"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                data-testid="event-description-input"
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="event-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.title.trim() || !form.date}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="event-dialog-submit"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
